/**
 * Meshes made ready to be bent: welded, finely cut, and told apart.
 *
 * The vehicles are drawn low-poly. A car's body is one side profile extruded
 * across the car, so its bonnet is two long triangles from wing to wing; a
 * truck's cab is a few boxes. Pushed in, a triangle that long only tilts: it
 * cannot dent, let alone fold. So before a panel is crushed it is:
 *
 * - **welded** (`weld`): corners at one place become one vertex, so pushing
 *   one moves every face that meets there and nothing tears open;
 * - **cut finer** (`tessellate`) until no edge is longer than a few
 *   centimetres, by Rivara's longest-edge bisection: an edge is split in
 *   both triangles that share it, so no vertex ever sits in the middle of a
 *   neighbour's edge, where it would pull a crack open when moved;
 * - **told apart** (`pieces`): triangles joined by shared vertices are one
 *   piece. A model often merges many parts into one mesh per material (the
 *   motorbike's plastics are one mesh), and it is pieces, not meshes, that
 *   break off.
 */

/** An indexed triangle mesh: x, y, z per vertex, three vertices per triangle. */
export interface TriMesh {
  positions: Float32Array;
  indices: Uint32Array;
}

/**
 * Merge vertices closer than `epsilon`: a box's corners, drawn once per face
 * for its normals, become one. Unindexed input (`indices` undefined) is read
 * as one triangle per three vertices.
 */
export function weld(positions: ArrayLike<number>, indices?: ArrayLike<number>, epsilon = 1e-4): TriMesh {
  const count = positions.length / 3;
  const remap = new Uint32Array(count);
  const out: number[] = [];
  const seen = new Map<string, number>();
  const q = (v: number) => Math.round(v / epsilon);
  for (let i = 0; i < count; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    const key = `${q(x)},${q(y)},${q(z)}`;
    let at = seen.get(key);
    if (at === undefined) {
      at = out.length / 3;
      seen.set(key, at);
      out.push(x, y, z);
    }
    remap[i] = at;
  }
  const triangles = indices === undefined ? count : indices.length;
  const tri: number[] = [];
  for (let t = 0; t + 2 < triangles; t += 3) {
    const a = remap[indices === undefined ? t : indices[t]];
    const b = remap[indices === undefined ? t + 1 : indices[t + 1]];
    const c = remap[indices === undefined ? t + 2 : indices[t + 2]];
    // Welding can fold a sliver flat onto one point: drop it.
    if (a === b || b === c || a === c) continue;
    tri.push(a, b, c);
  }
  return { positions: new Float32Array(out), indices: new Uint32Array(tri) };
}

const edgeKey = (a: number, b: number) => (a < b ? a * 4194304 + b : b * 4194304 + a);

/**
 * Cut a welded mesh until no edge is longer than `maxEdge`, by longest-edge
 * bisection: an edge is split at its middle in both triangles that share it,
 * so the mesh stays watertight. Shapes and areas are unchanged; only the
 * vertices are more, appended after the ones there were. `maxEdge` may be a
 * function of where a triangle's middle is, to cut finely only where it is
 * wanted (Infinity: not at all). `carry` is three more numbers per vertex,
 * split along with the positions: where each vertex began, say.
 * `maxTriangles` stops it short on a pathological input rather than run away.
 */
export function tessellate(
  mesh: TriMesh, maxEdge: number | ((x: number, y: number, z: number) => number), maxTriangles = 200000,
  carry?: Float32Array,
): TriMesh & { carry?: Float32Array } {
  const pos: number[] = Array.from(mesh.positions);
  const tri: number[] = Array.from(mesh.indices);
  const extra: number[] | undefined = carry === undefined ? undefined : Array.from(carry);
  // Every edge's triangles, by key.
  const edges = new Map<number, number[]>();
  const link = (t: number) => {
    for (let k = 0; k < 3; k++) {
      const key = edgeKey(tri[t * 3 + k], tri[t * 3 + ((k + 1) % 3)]);
      const list = edges.get(key);
      if (list === undefined) edges.set(key, [t]);
      else list.push(t);
    }
  };
  const unlink = (t: number) => {
    for (let k = 0; k < 3; k++) {
      const key = edgeKey(tri[t * 3 + k], tri[t * 3 + ((k + 1) % 3)]);
      const list = edges.get(key);
      if (list === undefined) continue;
      const i = list.indexOf(t);
      if (i >= 0) list.splice(i, 1);
      if (list.length === 0) edges.delete(key);
    }
  };
  const length2 = (a: number, b: number) =>
    (pos[a * 3] - pos[b * 3]) ** 2 + (pos[a * 3 + 1] - pos[b * 3 + 1]) ** 2 + (pos[a * 3 + 2] - pos[b * 3 + 2]) ** 2;
  for (let t = 0; t < tri.length / 3; t++) link(t);

  const limit = typeof maxEdge === 'number' ? () => maxEdge : (t: number) => {
    const a = tri[t * 3] * 3, b = tri[t * 3 + 1] * 3, c = tri[t * 3 + 2] * 3;
    return maxEdge((pos[a] + pos[b] + pos[c]) / 3, (pos[a + 1] + pos[b + 1] + pos[c + 1]) / 3, (pos[a + 2] + pos[b + 2] + pos[c + 2]) / 3);
  };
  // A triangle's longest edge, as its position 0..2; ties go to the larger
  // key, so two triangles agree on which of a shared pair is longest.
  const longest = (t: number): number => {
    let best = 0, bestLength = -1, bestKey = -1;
    for (let k = 0; k < 3; k++) {
      const a = tri[t * 3 + k], b = tri[t * 3 + ((k + 1) % 3)];
      const l = length2(a, b), key = edgeKey(a, b);
      if (l > bestLength + 1e-12 || (Math.abs(l - bestLength) <= 1e-12 && key > bestKey)) { best = k; bestLength = l; bestKey = key; }
    }
    return best;
  };
  const edgeOf = (t: number, k: number) => edgeKey(tri[t * 3 + k], tri[t * 3 + ((k + 1) % 3)]);
  /** Split every triangle on edge `key` at its middle, keeping each one's winding. */
  const bisect = (key: number) => {
    const list = edges.get(key);
    if (list === undefined) return;
    let m = -1;
    for (const s of [...list]) {
      const v = [tri[s * 3], tri[s * 3 + 1], tri[s * 3 + 2]];
      const k = v.findIndex((x, j) => edgeKey(x, v[(j + 1) % 3]) === key);
      const p = v[k], q = v[(k + 1) % 3], r = v[(k + 2) % 3];
      if (m < 0) {
        m = pos.length / 3;
        pos.push((pos[p * 3] + pos[q * 3]) / 2, (pos[p * 3 + 1] + pos[q * 3 + 1]) / 2, (pos[p * 3 + 2] + pos[q * 3 + 2]) / 2);
        extra?.push((extra[p * 3] + extra[q * 3]) / 2, (extra[p * 3 + 1] + extra[q * 3 + 1]) / 2, (extra[p * 3 + 2] + extra[q * 3 + 2]) / 2);
      }
      unlink(s);
      tri[s * 3] = p; tri[s * 3 + 1] = m; tri[s * 3 + 2] = r;
      const n = tri.length / 3;
      tri.push(m, q, r);
      link(s);
      link(n);
    }
  };

  // Rivara's longest-edge propagation: to split a triangle's longest edge,
  // walk across it to the neighbour; while that neighbour's own longest edge
  // is another one, go on across that. Where two triangles share their
  // longest edge, split both, and come back. Splitting a neighbour across an
  // edge that is not its longest makes slivers that never end.
  for (let t = 0; t < tri.length / 3 && tri.length / 3 < maxTriangles; t++) {
    while (tri.length / 3 < maxTriangles) {
      const k = longest(t);
      const max = limit(t);
      if (length2(tri[t * 3 + k], tri[t * 3 + ((k + 1) % 3)]) <= max * max) break;
      const path = [t];
      while (path.length > 0 && tri.length / 3 < maxTriangles) {
        const top = path[path.length - 1];
        const key = edgeOf(top, longest(top));
        const next = (edges.get(key) ?? []).find(o => o !== top);
        if (next === undefined || edgeOf(next, longest(next)) === key) {
          bisect(key);
          path.pop();
        } else if (path.includes(next)) {
          // Never round in a circle: split here.
          bisect(key);
          path.pop();
        } else {
          path.push(next);
        }
      }
    }
  }
  return { positions: new Float32Array(pos), indices: new Uint32Array(tri), carry: extra === undefined ? undefined : new Float32Array(extra) };
}

/**
 * Which piece each triangle belongs to: triangles sharing a vertex are one
 * piece. Returns the piece of every triangle, and how many there are.
 */
export function pieces(mesh: TriMesh): { of: Uint32Array; count: number } {
  const n = mesh.positions.length / 3;
  const parent = new Uint32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const tri = mesh.indices;
  for (let t = 0; t < tri.length; t += 3) {
    const a = find(tri[t]), b = find(tri[t + 1]), c = find(tri[t + 2]);
    parent[b] = a;
    parent[find(c)] = a;
  }
  const id = new Map<number, number>();
  const of = new Uint32Array(tri.length / 3);
  for (let t = 0; t < tri.length / 3; t++) {
    const root = find(tri[t * 3]);
    let k = id.get(root);
    if (k === undefined) id.set(root, k = id.size);
    of[t] = k;
  }
  return { of, count: id.size };
}
